import { useEffect, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router';
import {
  deleteLocationReview,
  getLocation,
  listAllLocationReviews,
  listAllLocationMedia,
  listCategories,
  listTags,
  deleteLocation,
  getApiErrorMessage,
  listLocationBestSeasons,
  listLocationDifficulties,
  listLocationRoadTypes,
  listCountries,
  listCounties,
  listLocationModerationQueue,
  listLocations,
  listMyLocationSuggestions,
  listRegions,
  listSettlements,
  type LocationItem,
  type LocationStatus,
} from '../lib/api';
import { extractTextFromContentBlocks, isContentBlockMediaTitle, renderContentBlocksHtml } from '../lib/contentBlocks';
import { LOCATIONS_TEMPLATE } from '../templates/locationsTemplate';

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const renderStarRow = (value: number | null | undefined): string => {
  if (typeof value !== 'number' || Number.isNaN(value) || value < 1) {
    return '<span class="text-[11px] text-slate-400">No rating</span>';
  }
  return Array.from({ length: 5 }, (_, index) =>
    index < value
      ? '<span class="text-amber-500">★</span>'
      : '<span class="text-slate-300">★</span>'
  ).join('');
};

const statusBadgeClass = (status: LocationStatus): string => {
  if (status === 'published') {
    return 'bg-emerald-50 text-emerald-700';
  }
  if (status === 'pending_review') {
    return 'bg-amber-50 text-amber-700';
  }
  if (status === 'rejected') {
    return 'bg-rose-50 text-rose-700';
  }
  if (status === 'approved') {
    return 'bg-blue-50 text-blue-700';
  }
  if (status === 'archived') {
    return 'bg-slate-200 text-slate-700';
  }
  return 'bg-slate-100 text-slate-700';
};

const statusLabel = (status: LocationStatus): string => {
  if (status === 'pending_review') return 'Pending Review';
  if (status === 'published') return 'Published';
  if (status === 'approved') return 'Approved';
  if (status === 'rejected') return 'Rejected';
  if (status === 'archived') return 'Archived';
  return 'Draft';
};

export default function LocationsPage() {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const template = useMemo(() => LOCATIONS_TEMPLATE, []);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const tbody = root.querySelector<HTMLTableSectionElement>('#locations-table-body');
    const searchInput = root.querySelector<HTMLInputElement>('#locations-search-input');
    const statusFilter = root.querySelector<HTMLSelectElement>('#locations-status-filter');
    const countryFilter = root.querySelector<HTMLSelectElement>('#locations-country-filter');
    const regionFilter = root.querySelector<HTMLSelectElement>('#locations-region-filter');
    const ratingMinInput = root.querySelector<HTMLInputElement>('#locations-rating-min-input');
    const featuredFilter = root.querySelector<HTMLSelectElement>('#locations-featured-filter');
    const tagsInput = root.querySelector<HTMLInputElement>('#locations-tags-input');
    const bboxInput = root.querySelector<HTMLInputElement>('#locations-bbox-input');
    const centerLatInput = root.querySelector<HTMLInputElement>('#locations-center-lat-input');
    const centerLonInput = root.querySelector<HTMLInputElement>('#locations-center-lon-input');
    const radiusInput = root.querySelector<HTMLInputElement>('#locations-radius-input');
    const sortSelect = root.querySelector<HTMLSelectElement>('#locations-sort-select');
    const resultsMeta = root.querySelector<HTMLElement>('#locations-results-meta');
    const prevBtn = root.querySelector<HTMLButtonElement>('#locations-prev-btn');
    const nextBtn = root.querySelector<HTMLButtonElement>('#locations-next-btn');
    const pageIndicator = root.querySelector<HTMLElement>('#locations-page-indicator');
    const resetFiltersBtn = root.querySelector<HTMLButtonElement>('#locations-reset-filters-btn');
    const kpiPublished = root.querySelector<HTMLElement>('#locations-kpi-published');
    const kpiPending = root.querySelector<HTMLElement>('#locations-kpi-pending');
    const kpiDraft = root.querySelector<HTMLElement>('#locations-kpi-draft');
    const kpiFeatured = root.querySelector<HTMLElement>('#locations-kpi-featured');
    const createBtn = root.querySelector<HTMLButtonElement>('#locations-create-btn');
    const viewOverlay = root.querySelector<HTMLElement>('#locations-view-overlay');
    const viewTitle = root.querySelector<HTMLElement>('#locations-view-title');
    const viewStatus = root.querySelector<HTMLElement>('#locations-view-status');
    const viewRating = root.querySelector<HTMLElement>('#locations-view-rating');
    const viewReviews = root.querySelector<HTMLElement>('#locations-view-reviews');
    const viewReviewsCount = root.querySelector<HTMLElement>('#locations-view-reviews-count');
    const viewReviewsList = root.querySelector<HTMLElement>('#locations-view-reviews-list');
    const viewCoordinates = root.querySelector<HTMLElement>('#locations-view-coordinates');
    const viewRegion = root.querySelector<HTMLElement>('#locations-view-region');
    const viewDescription = root.querySelector<HTMLElement>('#locations-view-description');
    const viewAttributes = root.querySelector<HTMLElement>('#locations-view-attributes');
    const viewSlug = root.querySelector<HTMLElement>('#locations-view-slug');
    const viewSource = root.querySelector<HTMLElement>('#locations-view-source');
    const viewSubmitter = root.querySelector<HTMLElement>('#locations-view-submitter');
    const viewLocationName = root.querySelector<HTMLElement>('#locations-view-location-name');
    const viewCounty = root.querySelector<HTMLElement>('#locations-view-county');
    const viewSettlement = root.querySelector<HTMLElement>('#locations-view-settlement');
    const viewSafetyNotes = root.querySelector<HTMLElement>('#locations-view-safety-notes');
    const viewAccessRules = root.querySelector<HTMLElement>('#locations-view-access-rules');
    const viewFacts = root.querySelector<HTMLElement>('#locations-view-facts');
    const viewReviewComment = root.querySelector<HTMLElement>('#locations-view-review-comment');
    const viewArchived = root.querySelector<HTMLElement>('#locations-view-archived');
    const viewCreatedAt = root.querySelector<HTMLElement>('#locations-view-created-at');
    const viewUpdatedAt = root.querySelector<HTMLElement>('#locations-view-updated-at');
    const viewPublishedAt = root.querySelector<HTMLElement>('#locations-view-published-at');
    const viewRatingCount = root.querySelector<HTMLElement>('#locations-view-rating-count');
    const viewHeroImage = root.querySelector<HTMLImageElement>('#locations-view-hero-image');
    const viewThumbnails = root.querySelector<HTMLElement>('#locations-view-thumbnails');
    const viewCloseBtn = root.querySelector<HTMLButtonElement>('#locations-view-close-btn');
    const viewEditBtn = root.querySelector<HTMLButtonElement>('#locations-view-edit-btn');
    const viewDeleteBtn = root.querySelector<HTMLButtonElement>('#locations-view-delete-btn');

    if (
      !tbody ||
      !searchInput ||
      !statusFilter ||
      !countryFilter ||
      !regionFilter ||
      !ratingMinInput ||
      !featuredFilter ||
      !tagsInput ||
      !bboxInput ||
      !centerLatInput ||
      !centerLonInput ||
      !radiusInput ||
      !sortSelect ||
      !resultsMeta ||
      !prevBtn ||
      !nextBtn ||
      !pageIndicator ||
      !resetFiltersBtn ||
      !kpiPublished ||
      !kpiPending ||
      !kpiDraft ||
      !kpiFeatured ||
      !createBtn ||
      !viewOverlay ||
      !viewTitle ||
      !viewStatus ||
      !viewRating ||
      !viewReviews ||
      !viewReviewsCount ||
      !viewReviewsList ||
      !viewCoordinates ||
      !viewRegion ||
      !viewDescription ||
      !viewAttributes ||
      !viewSlug ||
      !viewSource ||
      !viewSubmitter ||
      !viewLocationName ||
      !viewCounty ||
      !viewSettlement ||
      !viewSafetyNotes ||
      !viewAccessRules ||
      !viewFacts ||
      !viewReviewComment ||
      !viewArchived ||
      !viewCreatedAt ||
      !viewUpdatedAt ||
      !viewPublishedAt ||
      !viewRatingCount ||
      !viewHeroImage ||
      !viewThumbnails ||
      !viewCloseBtn ||
      !viewEditBtn ||
      !viewDeleteBtn
    ) {
      return;
    }

    let disposed = false;
    let viewRequestId = 0;
    let searchTimer: number | null = null;
    const queryPage = Number(new URLSearchParams(window.location.search).get('page') || '1');
    let currentPage = Number.isFinite(queryPage) && queryPage > 0 ? queryPage : 1;
    const pageSize = 20;

    const syncQueryParams = (page: number) => {
      const params = new URLSearchParams(window.location.search);
      params.set('page', String(page));

      const search = searchInput.value.trim();
      if (search) params.set('search', search);
      else params.delete('search');

      const status = statusFilter.value.trim();
      if (status && status !== 'all') params.set('status_filter', status);
      else params.delete('status_filter');

      const country = countryFilter.value.trim();
      if (country && country !== 'all') params.set('country_filter', country);
      else params.delete('country_filter');

      const region = regionFilter.value.trim();
      if (region && region !== 'all') params.set('region_filter', region);
      else params.delete('region_filter');

      const ratingMin = ratingMinInput.value.trim();
      if (ratingMin) params.set('rating_min', ratingMin);
      else params.delete('rating_min');

      const featured = featuredFilter.value.trim();
      if (featured && featured !== 'all') params.set('featured_filter', featured);
      else params.delete('featured_filter');

      const tags = tagsInput.value.trim();
      if (tags) params.set('tags_filter', tags);
      else params.delete('tags_filter');

      const bbox = bboxInput.value.trim();
      if (bbox) params.set('bbox_filter', bbox);
      else params.delete('bbox_filter');

      const centerLat = centerLatInput.value.trim();
      const centerLon = centerLonInput.value.trim();
      const radius = radiusInput.value.trim();
      if (centerLat) params.set('center_lat_filter', centerLat);
      else params.delete('center_lat_filter');
      if (centerLon) params.set('center_lon_filter', centerLon);
      else params.delete('center_lon_filter');
      if (radius) params.set('radius_filter', radius);
      else params.delete('radius_filter');

      const ordering = sortSelect.value.trim();
      if (ordering && ordering !== '-created_at') params.set('ordering_filter', ordering);
      else params.delete('ordering_filter');

      const query = params.toString();
      const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}`;
      window.history.replaceState(window.history.state, '', nextUrl);
    };

    const buildReturnTo = (): string => {
      syncQueryParams(currentPage);
      return `${window.location.pathname}${window.location.search}`;
    };

    const parseSelectedId = (value: string): number | undefined => {
      if (!value || value === 'all') return undefined;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : undefined;
    };

    const selectedListMode = (): 'all' | 'my' | 'queue' => {
      const value = statusFilter.value;
      if (value === 'my_suggestions') return 'my';
      if (value === 'moderation_queue') return 'queue';
      return 'all';
    };

    const toOrdering = (value: string): string | undefined => {
      if (!value || value === 'default') return '-created_at';
      return value;
    };

    const updateViewQueryParam = (value: string | null) => {
      const params = new URLSearchParams(window.location.search);
      if (value) {
        params.set('view', value);
      } else {
        params.delete('view');
      }
      const query = params.toString();
      const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}`;
      window.history.replaceState(window.history.state, '', nextUrl);
    };

    const closeView = () => {
      viewRequestId += 1;
      viewOverlay.classList.add('hidden');
      updateViewQueryParam(null);
    };

    const toLabel = (value: string): string => {
      if (!value) return '';
      return value
        .split(',')
        .map((part) =>
          part
            .trim()
            .split(/[_\s-]+/)
            .map((chunk) => (chunk ? chunk[0].toUpperCase() + chunk.slice(1).toLowerCase() : ''))
            .join(' ')
        )
        .filter(Boolean)
        .join(', ');
    };

    const formatDateTime = (value: string | null | undefined): string => {
      if (!value) return '-';
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return value;
      return parsed.toLocaleString();
    };

    const normalizeMediaUrl = (rawUrl: string): string => {
      if (/^https?:\/\//i.test(rawUrl)) return rawUrl;
      if (rawUrl.startsWith('/')) return `${window.location.origin}${rawUrl}`;
      return rawUrl;
    };

    const renderViewMedia = (mediaItems: Array<{ primary: string; fallback?: string | null }>) => {
      if (!mediaItems.length) {
        viewHeroImage.src =
          "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='1200' height='675'><rect width='100%' height='100%' fill='%23e2e8f0'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='%2364758b' font-size='30' font-family='Arial'>No media</text></svg>";
        viewThumbnails.innerHTML = '';
        return;
      }

      const items = mediaItems.map((item) => ({
        primary: normalizeMediaUrl(item.primary),
        fallback: item.fallback ? normalizeMediaUrl(item.fallback) : null,
      }));
      viewHeroImage.src = items[0].primary;
      viewHeroImage.onerror = () => {
        const fb = items[0].fallback;
        if (fb && viewHeroImage.src !== fb) {
          viewHeroImage.src = fb;
          return;
        }
        viewHeroImage.src =
          "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='1200' height='675'><rect width='100%' height='100%' fill='%23e2e8f0'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='%2364758b' font-size='30' font-family='Arial'>No media</text></svg>";
      };

      viewThumbnails.innerHTML = items
        .slice(0, 4)
        .map(
          (item, index) => `
          <div class="aspect-square rounded-lg bg-slate-100 overflow-hidden ${index === 0 ? 'ring-2 ring-primary' : ''}">
            <img class="h-full w-full object-cover" alt="Location media" src="${escapeHtml(item.primary)}" data-fallback="${escapeHtml(item.fallback || '')}"/>
          </div>
        `
        )
        .join('');

      viewThumbnails.querySelectorAll<HTMLImageElement>('img[data-fallback]').forEach((img) => {
        img.onerror = () => {
          const fb = img.dataset.fallback || '';
          if (fb && img.src !== fb) {
            img.src = fb;
            return;
          }
          img.style.visibility = 'hidden';
        };
      });
    };

    const renderRows = (items: LocationItem[]) => {
      if (items.length === 0) {
        tbody.innerHTML = `
          <tr>
            <td colspan="7" class="px-6 py-10 text-center text-sm text-slate-500">
              No locations found for selected filters.
            </td>
          </tr>
        `;
        return;
      }

      tbody.innerHTML = items
        .map((item) => {
          const locationSubtitle = item.location_name || `Lat ${item.latitude}, Lon ${item.longitude}`;
          const isFeaturedIcon = item.is_featured
            ? '<span class="material-symbols-outlined text-amber-400 fill-1">star</span>'
            : '<span class="material-symbols-outlined text-slate-300">star</span>';
          return `
            <tr class="hover:bg-slate-50/80 dark:hover:bg-slate-800/30 transition-colors">
              <td class="px-6 py-3 text-[12px] text-slate-500 font-mono">#${item.id}</td>
              <td class="px-6 py-3">
                <button data-action="view" data-location-id="${item.id}" class="font-bold text-[15px] text-slate-900 hover:text-primary text-left leading-tight" type="button">${escapeHtml(item.title)}</button>
                <div class="text-[12px] text-slate-500 flex items-center gap-1 mt-0.5">
                  <span class="material-symbols-outlined text-[14px]">location_on</span>${escapeHtml(locationSubtitle)}
                </div>
              </td>
              <td class="px-6 py-3">
                <span class="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold ${statusBadgeClass(item.status)}">
                  ${statusLabel(item.status)}
                </span>
              </td>
              <td class="px-6 py-3 text-center">${isFeaturedIcon}</td>
              <td class="px-6 py-3">
                <div class="flex flex-col">
                  <div class="flex items-center gap-1 text-[15px] font-semibold leading-none">
                    <span class="material-symbols-outlined text-[14px] text-amber-500 fill-1">star</span>${item.rating_avg}
                  </div>
                  <div class="text-[11px] text-slate-400 uppercase whitespace-nowrap">${item.reviews_count} Rev / ${item.favorites_count} Fav</div>
                </div>
              </td>
              <td class="px-6 py-3">
                <span class="text-[15px]">${escapeHtml(item.submitter_display || item.submitter_username || item.source)}</span>
              </td>
              <td class="px-6 py-3">
                <div class="flex items-center justify-end gap-1 whitespace-nowrap">
                  <button data-action="view" data-location-id="${item.id}" class="p-1.5 hover:bg-slate-100 rounded-md text-slate-400 hover:text-primary transition-colors" type="button">
                    <span class="material-symbols-outlined text-[20px] pointer-events-none">visibility</span>
                  </button>
                  <button data-action="edit" data-location-id="${item.id}" class="p-1.5 hover:bg-slate-100 rounded-md text-slate-400 hover:text-blue-500 transition-colors" type="button">
                    <span class="material-symbols-outlined text-[20px] pointer-events-none">edit</span>
                  </button>
                  <button data-action="delete" data-location-id="${item.id}" class="p-1.5 hover:bg-slate-100 rounded-md text-slate-400 hover:text-red-500 transition-colors" type="button">
                    <span class="material-symbols-outlined text-[20px] pointer-events-none">delete</span>
                  </button>
                </div>
              </td>
            </tr>
          `;
        })
        .join('');
    };

    const openView = async (locationId: number) => {
      const requestId = ++viewRequestId;
      viewOverlay.classList.remove('hidden');
      viewTitle.textContent = 'Loading...';
      viewStatus.textContent = 'Loading';
      viewHeroImage.src =
        "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='1200' height='675'><rect width='100%' height='100%' fill='%23e2e8f0'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='%2364758b' font-size='30' font-family='Arial'>Loading</text></svg>";
      viewDescription.innerHTML = '<p>Loading...</p>';
      viewAttributes.innerHTML = '';
      viewThumbnails.innerHTML = '';
      viewSlug.textContent = '-';
      viewSource.textContent = '-';
      viewSubmitter.textContent = '-';
      viewLocationName.textContent = '-';
      viewCounty.textContent = '-';
      viewSettlement.textContent = '-';
      viewSafetyNotes.textContent = '-';
      viewAccessRules.textContent = '-';
      viewFacts.innerHTML = '-';
      viewReviewComment.textContent = '-';
      viewReviewsCount.textContent = '0';
      viewReviewsList.innerHTML = '<p class="text-sm text-slate-500">Loading reviews...</p>';
      viewArchived.textContent = '-';
      viewCreatedAt.textContent = '-';
      viewUpdatedAt.textContent = '-';
      viewPublishedAt.textContent = '-';
      viewRatingCount.textContent = '-';

      try {
        const [item, media, reviews, countries, regions, counties, settlements, categories, tags, roadTypes, difficulties, bestSeasons] = await Promise.all([
          getLocation(locationId),
          listAllLocationMedia(locationId),
          listAllLocationReviews(locationId),
          listCountries(),
          listRegions(),
          listCounties(),
          listSettlements(),
          listCategories(),
          listTags(),
          listLocationRoadTypes(),
          listLocationDifficulties(),
          listLocationBestSeasons(),
        ]);
        if (disposed || requestId !== viewRequestId) return;

        const countryName = countries.find((x) => x.id === item.country)?.name ?? '';
        const regionName = regions.find((x) => x.id === item.region)?.name ?? '';
        const countyName = counties.find((x) => x.id === item.county)?.name ?? '';
        const settlementName = settlements.find((x) => x.id === item.settlement)?.name ?? '';
        const categoryNames = categories.filter((x) => item.category_ids.includes(x.id)).map((x) => x.name);
        const tagNames = tags.filter((x) => item.tag_ids.includes(x.id)).map((x) => x.name);
        const roadTypeName =
          item.road_type_display ||
          roadTypes.find((entry) => entry.id === item.road_type)?.name ||
          '';
        const difficultyName =
          item.difficulty_display ||
          difficulties.find((entry) => entry.id === item.difficulty)?.name ||
          '';
        const bestSeasonName =
          item.best_season_display ||
          bestSeasons.find((entry) => entry.id === item.best_season)?.name ||
          '';
        const attributes = [roadTypeName, difficultyName, bestSeasonName, ...categoryNames, ...tagNames].filter(Boolean);
        const mediaItems = media.results
          .filter((entry) => !isContentBlockMediaTitle(entry.title))
          .sort((a, b) => a.sort_order - b.sort_order)
          .map((entry) => ({ primary: entry.file_large_url || entry.file_url, fallback: entry.file_url }))
          .filter((entry) => Boolean(entry.primary)) as Array<{ primary: string; fallback?: string | null }>;

        viewTitle.textContent = item.title;
        viewStatus.textContent = statusLabel(item.status);
        viewRating.textContent = item.rating_avg;
        viewReviews.textContent = `(${item.reviews_count} Reviews)`;
        viewCoordinates.textContent = `${item.latitude}°, ${item.longitude}°`;
        viewRegion.textContent = [countryName, regionName].filter(Boolean).join(', ') || item.location_name || '-';
        viewSlug.textContent = item.slug || '-';
        viewSource.textContent = toLabel(item.source) || '-';
        viewSubmitter.textContent = item.submitter_display || item.submitter_username || '-';
        viewLocationName.textContent = item.location_name || '-';
        viewCounty.textContent = countyName || '-';
        viewSettlement.textContent = settlementName || '-';
        viewSafetyNotes.textContent = item.safety_notes?.trim() || '-';
        viewAccessRules.textContent = item.access_rules?.trim() || '-';
        viewFacts.innerHTML = item.facts?.length
          ? item.facts
              .sort((a, b) => a.sort_order - b.sort_order)
              .map((fact) => `<p>• ${escapeHtml(fact.text)}</p>`)
              .join('')
          : '<p>-</p>';
        viewReviewComment.textContent = item.review_comment?.trim() || '-';
        const reviewsSummary = reviews.summary ?? item.reviews_summary;
        viewReviewsCount.textContent = reviewsSummary
          ? `${reviewsSummary.average_rating.toFixed(1)} avg • ${reviewsSummary.reviews_count} reviews`
          : String(reviews.count);
        viewReviewsList.innerHTML = reviews.results.length
          ? reviews.results
              .slice(0, 20)
              .map(
                (review) => `
                  <article class="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    <div class="mb-2 flex items-start justify-between gap-3">
                      <div class="flex min-w-0 items-center gap-2">
                        ${
                          review.author.avatar
                            ? `<img class="h-8 w-8 rounded-full object-cover border border-slate-200" src="${escapeHtml(
                                review.author.avatar
                              )}" alt="${escapeHtml(review.author.name)}"/>`
                            : `<div class="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-[11px] font-bold text-slate-600">${escapeHtml(
                                (review.author.name || '?').slice(0, 1).toUpperCase()
                              )}</div>`
                        }
                        <div class="min-w-0">
                          <p class="truncate text-xs font-semibold text-slate-700">${escapeHtml(
                            review.author.name || 'Unknown user'
                          )}</p>
                          <div class="text-[11px] leading-none">${renderStarRow(review.rating_value)}</div>
                        </div>
                      </div>
                      <span class="inline-flex rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-700">${escapeHtml(review.status)}</span>
                    </div>
                    <p class="text-sm text-slate-700">${escapeHtml((review.message || '').trim() || 'No text provided.')}</p>
                    <div class="mt-1 flex items-center justify-between gap-3">
                      <p class="text-[11px] text-slate-500">${escapeHtml(formatDateTime(review.created_at))}</p>
                      <button
                        type="button"
                        class="inline-flex items-center rounded border border-red-200 bg-red-50 px-2 py-1 text-[11px] font-semibold text-red-700"
                        data-review-delete-id="${review.id}"
                      >
                        Delete
                      </button>
                    </div>
                  </article>
                `
              )
              .join('')
          : '<p class="text-sm text-slate-500">No reviews yet.</p>';
        viewArchived.textContent = item.is_archived ? 'Yes' : 'No';
        viewCreatedAt.textContent = formatDateTime(item.created_at);
        viewUpdatedAt.textContent = formatDateTime(item.updated_at);
        viewPublishedAt.textContent = formatDateTime(item.published_at);
        viewRatingCount.textContent = String(item.rating_count ?? 0);
        const renderedBlocks = renderContentBlocksHtml(item.content_blocks);
        if (renderedBlocks) {
          viewDescription.innerHTML = renderedBlocks;
        } else {
          const fallbackDescription = item.description || extractTextFromContentBlocks(item.content_blocks);
          viewDescription.innerHTML =
            fallbackDescription
              .split('\n')
              .filter((part) => part.trim().length > 0)
              .map((part) => `<p>${escapeHtml(part)}</p>`)
              .join('') || '<p>No description provided.</p>';
        }
        viewAttributes.innerHTML = attributes.length
          ? attributes.map((value) => `<span class="inline-flex items-center gap-1 rounded-md bg-primary/5 px-2.5 py-1 text-xs font-medium text-primary">${escapeHtml(value)}</span>`).join('')
          : '<span class="text-xs text-slate-500">No attributes</span>';
        renderViewMedia(mediaItems);
        viewEditBtn.setAttribute('data-location-id', String(item.id));
        viewDeleteBtn.setAttribute('data-location-id', String(item.id));
      } catch (error) {
        if (disposed || requestId !== viewRequestId) return;
        viewTitle.textContent = 'Failed to load location';
        viewStatus.textContent = 'Error';
        viewDescription.innerHTML = `<p>${escapeHtml(getApiErrorMessage(error, 'Unable to fetch location details.'))}</p>`;
      }
    };

    const loadLocations = async (page = currentPage) => {
      currentPage = Math.max(1, page);
      syncQueryParams(currentPage);
      tbody.innerHTML = `
        <tr>
          <td colspan="7" class="px-6 py-10 text-center text-sm text-slate-500">Loading locations...</td>
        </tr>
      `;

      try {
        let results: LocationItem[] = [];
        let count = 0;
        let hasPrevious = false;
        let hasNext = false;
        if (selectedListMode() === 'my') {
          const payload = await listMyLocationSuggestions({ page: currentPage, page_size: pageSize });
          results = payload.results;
          count = payload.count;
          hasPrevious = Boolean(payload.previous);
          hasNext = Boolean(payload.next);
        } else if (selectedListMode() === 'queue') {
          const payload = await listLocationModerationQueue({ page: currentPage, page_size: pageSize });
          results = payload.results;
          count = payload.count;
          hasPrevious = Boolean(payload.previous);
          hasNext = Boolean(payload.next);
        } else {
          const params: Record<string, string | number | boolean | undefined> = {
            search: searchInput.value.trim() || undefined,
            ordering: toOrdering(sortSelect.value),
            page: currentPage,
            page_size: pageSize,
          };

          const statusValue = statusFilter.value;
          if (['published', 'pending_review', 'draft', 'approved', 'rejected', 'archived'].includes(statusValue)) {
            params.status = statusValue;
          }

          const countryId = parseSelectedId(countryFilter.value);
          if (countryId) params.country = countryId;

          const regionId = parseSelectedId(regionFilter.value);
          if (regionId) params.region = regionId;
          if (ratingMinInput.value.trim()) params.rating_min = ratingMinInput.value.trim();
          if (tagsInput.value.trim()) params.tags = tagsInput.value.trim();
          if (bboxInput.value.trim()) params.bbox = bboxInput.value.trim();

          const featuredValue = featuredFilter.value;
          if (featuredValue === 'yes') params.featured = true;
          if (featuredValue === 'no') params.featured = false;

          const centerLat = centerLatInput.value.trim();
          const centerLon = centerLonInput.value.trim();
          const radius = radiusInput.value.trim();
          if (centerLat && centerLon && radius) {
            params.center_lat = centerLat;
            params.center_lon = centerLon;
            params.radius_km = radius;
          }

          const payload = await listLocations(params);
          results = payload.results;
          count = payload.count;
          hasPrevious = Boolean(payload.previous);
          hasNext = Boolean(payload.next);
        }
        if (disposed) return;

        renderRows(results);
        const from = count === 0 ? 0 : (currentPage - 1) * pageSize + 1;
        const to = count === 0 ? 0 : Math.min(from + results.length - 1, count);
        resultsMeta.innerHTML = `Showing <span class="font-semibold">${from}-${to}</span> of <span class="font-semibold">${count}</span> results`;
        pageIndicator.textContent = `Page ${currentPage}`;
        prevBtn.disabled = !hasPrevious;
        nextBtn.disabled = !hasNext;
      } catch (error) {
        if (disposed) return;
        const message = getApiErrorMessage(error, 'Failed to load locations.');
        tbody.innerHTML = `
          <tr>
            <td colspan="7" class="px-6 py-10 text-center text-sm text-red-600">${escapeHtml(message)}</td>
          </tr>
        `;
        resultsMeta.textContent = 'Showing 0 of 0 results';
        pageIndicator.textContent = `Page ${currentPage}`;
        prevBtn.disabled = true;
        nextBtn.disabled = true;
      }
    };

    const loadDictionaries = async () => {
      try {
        const [countries, regions] = await Promise.all([listCountries(), listRegions()]);
        if (disposed) return;

        countryFilter.innerHTML = `<option value="all">Country: All</option>${countries
          .map((item) => `<option value="${item.id}">${escapeHtml(item.name)}</option>`)
          .join('')}`;

        regionFilter.innerHTML = `<option value="all">Region: All</option>${regions
          .map((item) => `<option value="${item.id}">${escapeHtml(item.name)}</option>`)
          .join('')}`;

        const stateParams = new URLSearchParams(window.location.search);
        const presetCountry = stateParams.get('country_filter');
        const presetRegion = stateParams.get('region_filter');
        if (presetCountry) {
          countryFilter.value = presetCountry;
        }
        if (presetRegion) {
          regionFilter.value = presetRegion;
        }
      } catch {
        // Keep template defaults if dictionaries are not available.
      }
    };

    const loadCounters = async () => {
      try {
        const [published, pending, draft, featured] = await Promise.all([
          listLocations({ status: 'published', page: 1, page_size: 1 }),
          listLocations({ status: 'pending_review', page: 1, page_size: 1 }),
          listLocations({ status: 'draft', page: 1, page_size: 1 }),
          listLocations({ featured: true, page: 1, page_size: 1 }),
        ]);
        if (disposed) return;
        kpiPublished.textContent = String(published.count);
        kpiPending.textContent = String(pending.count);
        kpiDraft.textContent = String(draft.count);
        kpiFeatured.textContent = String(featured.count);
      } catch {
        if (disposed) return;
        kpiPublished.textContent = '-';
        kpiPending.textContent = '-';
        kpiDraft.textContent = '-';
        kpiFeatured.textContent = '-';
      }
    };

    const onFilterChange = () => {
      currentPage = 1;
      void loadLocations(1);
    };

    const onSearchInput = () => {
      if (searchTimer) {
        window.clearTimeout(searchTimer);
      }
      searchTimer = window.setTimeout(() => {
        currentPage = 1;
        void loadLocations(1);
      }, 350);
    };
    const onPrevPage = () => {
      if (currentPage <= 1) return;
      void loadLocations(currentPage - 1);
    };
    const onNextPage = () => {
      void loadLocations(currentPage + 1);
    };

    const onActionClick = (event: Event) => {
      const target = event.target as HTMLElement;
      const button = target.closest<HTMLButtonElement>('button[data-action][data-location-id]');
      if (!button) return;
      const id = button.getAttribute('data-location-id');
      if (!id) return;
      if (button.dataset.action === 'delete') {
        const confirmed = window.confirm(`Delete location #${id}?`);
        if (!confirmed) return;
        void deleteLocation(Number(id))
          .then(() => loadLocations())
          .catch((error) => {
            const message = getApiErrorMessage(error, 'Failed to delete location.');
            window.alert(message);
          });
        return;
      }
      if (button.dataset.action === 'view' || button.dataset.action === 'edit') {
        if (button.dataset.action === 'edit') {
          const returnTo = encodeURIComponent(buildReturnTo());
          navigate(`/locations/create?id=${id}&return=${returnTo}`);
          return;
        }
        updateViewQueryParam(id);
        void openView(Number(id));
      }
    };

    const onCreate = () => {
      const returnTo = encodeURIComponent(buildReturnTo());
      navigate(`/locations/create?return=${returnTo}`);
    };
    const onResetFilters = () => {
      searchInput.value = '';
      statusFilter.value = 'all';
      countryFilter.value = 'all';
      regionFilter.value = 'all';
      ratingMinInput.value = '';
      featuredFilter.value = 'all';
      tagsInput.value = '';
      bboxInput.value = '';
      centerLatInput.value = '';
      centerLonInput.value = '';
      radiusInput.value = '';
      sortSelect.value = '-created_at';
      currentPage = 1;
      void loadLocations(1);
    };
    const onViewClose = () => closeView();
    const onViewOverlayClick = (event: Event) => {
      if (event.target === viewOverlay) closeView();
    };
    const onViewEdit = () => {
      const id = viewEditBtn.getAttribute('data-location-id');
      if (id) {
        const returnTo = encodeURIComponent(buildReturnTo());
        navigate(`/locations/create?id=${id}&return=${returnTo}`);
      }
    };
    const onViewDelete = () => {
      const id = viewDeleteBtn.getAttribute('data-location-id');
      if (!id) return;
      const confirmed = window.confirm(`Delete location #${id}?`);
      if (!confirmed) return;
      void deleteLocation(Number(id))
        .then(() => {
          closeView();
          return loadLocations();
        })
        .catch((error) => {
          window.alert(getApiErrorMessage(error, 'Failed to delete location.'));
        });
    };
    const onViewReviewDelete = (event: Event) => {
      const target = event.target as HTMLElement;
      const button = target.closest<HTMLButtonElement>('[data-review-delete-id]');
      if (!button) return;
      const reviewId = button.getAttribute('data-review-delete-id');
      const locationId = viewDeleteBtn.getAttribute('data-location-id');
      if (!reviewId || !locationId) return;
      const confirmed = window.confirm(`Delete review #${reviewId}?`);
      if (!confirmed) return;
      void deleteLocationReview(Number(locationId), Number(reviewId))
        .then(async () => {
          await openView(Number(locationId));
          await loadLocations(currentPage);
        })
        .catch((error) => {
          window.alert(getApiErrorMessage(error, 'Failed to delete review.'));
        });
    };

    tbody.addEventListener('click', onActionClick);
    searchInput.addEventListener('input', onSearchInput);
    statusFilter.addEventListener('change', onFilterChange);
    countryFilter.addEventListener('change', onFilterChange);
    regionFilter.addEventListener('change', onFilterChange);
    ratingMinInput.addEventListener('input', onSearchInput);
    featuredFilter.addEventListener('change', onFilterChange);
    tagsInput.addEventListener('input', onSearchInput);
    bboxInput.addEventListener('input', onSearchInput);
    centerLatInput.addEventListener('input', onSearchInput);
    centerLonInput.addEventListener('input', onSearchInput);
    radiusInput.addEventListener('input', onSearchInput);
    sortSelect.addEventListener('change', onFilterChange);
    prevBtn.addEventListener('click', onPrevPage);
    nextBtn.addEventListener('click', onNextPage);
    createBtn.addEventListener('click', onCreate);
    resetFiltersBtn.addEventListener('click', onResetFilters);
    viewCloseBtn.addEventListener('click', onViewClose);
    viewOverlay.addEventListener('click', onViewOverlayClick);
    viewEditBtn.addEventListener('click', onViewEdit);
    viewDeleteBtn.addEventListener('click', onViewDelete);
    viewReviewsList.addEventListener('click', onViewReviewDelete);

    statusFilter.innerHTML = `
      <option value="all">Status: All</option>
      <option value="draft">Draft</option>
      <option value="pending_review">Pending Review</option>
      <option value="approved">Approved</option>
      <option value="published">Published</option>
      <option value="rejected">Rejected</option>
      <option value="archived">Archived</option>
      <option value="my_suggestions">My Suggestions</option>
      <option value="moderation_queue">Moderation Queue</option>
    `;

    // Restore list state from URL query when returning from edit page.
    const stateParams = new URLSearchParams(window.location.search);
    searchInput.value = stateParams.get('search') || '';
    statusFilter.value = stateParams.get('status_filter') || 'all';
    ratingMinInput.value = stateParams.get('rating_min') || '';
    featuredFilter.value = stateParams.get('featured_filter') || 'all';
    tagsInput.value = stateParams.get('tags_filter') || '';
    bboxInput.value = stateParams.get('bbox_filter') || '';
    centerLatInput.value = stateParams.get('center_lat_filter') || '';
    centerLonInput.value = stateParams.get('center_lon_filter') || '';
    radiusInput.value = stateParams.get('radius_filter') || '';
    sortSelect.value = stateParams.get('ordering_filter') || '-created_at';
    void (async () => {
      await loadDictionaries();
      await Promise.all([loadLocations(currentPage), loadCounters()]);
    })();
    const initialView = new URLSearchParams(window.location.search).get('view');
    if (initialView && /^\d+$/.test(initialView)) {
      void openView(Number(initialView));
    }

    return () => {
      disposed = true;
      if (searchTimer) {
        window.clearTimeout(searchTimer);
      }
      tbody.removeEventListener('click', onActionClick);
      searchInput.removeEventListener('input', onSearchInput);
      statusFilter.removeEventListener('change', onFilterChange);
      countryFilter.removeEventListener('change', onFilterChange);
      regionFilter.removeEventListener('change', onFilterChange);
      ratingMinInput.removeEventListener('input', onSearchInput);
      featuredFilter.removeEventListener('change', onFilterChange);
      tagsInput.removeEventListener('input', onSearchInput);
      bboxInput.removeEventListener('input', onSearchInput);
      centerLatInput.removeEventListener('input', onSearchInput);
      centerLonInput.removeEventListener('input', onSearchInput);
      radiusInput.removeEventListener('input', onSearchInput);
      sortSelect.removeEventListener('change', onFilterChange);
      prevBtn.removeEventListener('click', onPrevPage);
      nextBtn.removeEventListener('click', onNextPage);
      createBtn.removeEventListener('click', onCreate);
      resetFiltersBtn.removeEventListener('click', onResetFilters);
      viewCloseBtn.removeEventListener('click', onViewClose);
      viewOverlay.removeEventListener('click', onViewOverlayClick);
      viewEditBtn.removeEventListener('click', onViewEdit);
      viewDeleteBtn.removeEventListener('click', onViewDelete);
      viewReviewsList.removeEventListener('click', onViewReviewDelete);
    };
  }, [navigate]);

  return (
    <>
      <Helmet>
        <title>MotoJourney | Locations</title>
      </Helmet>
      <div className="font-display bg-background-light text-slate-900 min-h-screen">
        <div ref={containerRef} dangerouslySetInnerHTML={{ __html: template }} />
      </div>
    </>
  );
}
